import { useEffectAsync } from '../../helpers';
import FirstUpper from '../FirstUpper';
import apiClient from '../../Services/ApiClient';
import { USER_FULL_INFO } from '../UserInfo/UserData';
import React, { useState } from 'react';
import UserAvatar from '../UserAvatar/UserAvatar';
// @ts-ignore
import styles from './PatientMiniProfile.module.scss';
import Loading from '../Loading';
import { useAppContext } from '../../Services/AppContext';
import FormattedDate from '../FormattedDate';
import TreatmentModal from '../Treatment/TreatmentModal';
import { useTreatmentContext } from '../../Services/TreatmentContext';
import {
	useLazyFullUserInfoQuery,
	useLazyHealthPlanUserBabyQuery,
	User,
} from '../../graphql/graphql';
import useTranslation from '../../Hooks/useTranslation';
import PatientPsychoQuestionnaireWarning from '../../Modules/Patient/Components/PatientWarning/PatientPsychoQuestionnaireWarning';
import Text from '../Base/Text/Text';
import usePatientMeta from '../PatientProfile/Hooks/usePatientMeta';
import UserAvatarImage from '../UserAvatar/UserAvatarImage';
import { getMomentDate } from '../../Utils/dates.util';
import usePatientIdentification from '../../Modules/Patient/Hooks/usePatientIdentification';

interface PatientMiniProfileProps {
	userId: string;
	workplaceId: string;
	loggedUserId: string;
	showVerifyInformation: boolean | null;
	links: object | null;
	showAge: boolean | null;
	showBirthDate: boolean | null;
	showAddress: boolean | null;
	showPhone: boolean | null;
	showEmail: boolean | null;
	showInsuranceCompany: boolean | null;
	showGender: boolean | null;
	allowOpenProfile?: boolean;
}

export default function PatientMiniProfile(props: PatientMiniProfileProps) {
	const {
		userId,
		workplaceId,
		loggedUserId,
		allowOpenProfile = true,
		showVerifyInformation = false,
		links,
		showAge,
		showAddress,
		showBirthDate,
		showPhone,
		showEmail,
		showInsuranceCompany,
		showGender,
	} = props;
	const [user, setUser] = useState(null);
	const { translation, isLatam, dateFormatMoment } = useAppContext();
	const [profileOpen, setProfileOpen] = useState(false);
	const [healthPlanBabyPregnancy, setHealthPlanBabyPregnancy] = useState<any>();
	const { userUpdated } = useTreatmentContext();
	const [getHealthPlanBabyPregnancy] = useLazyHealthPlanUserBabyQuery();
	const [getFullUserInfo] = useLazyFullUserInfoQuery();
	const { t } = useTranslation();
	const { identification } = usePatientIdentification(user);

	const {
		getPersonActingOnBehalfOfAPatient,
		hasPersonActingOnBehalfOfAPatient,
		patientDiagnosis,
		metaIsLoading,
	} = usePatientMeta(userId);

	const loadUser = async () => {
		let result;
		let tempUser: User | null = null;
		if (userId) {
			result = await getFullUserInfo({ id: userId });
			if ('data' in result) {
				tempUser = result?.data?.user;
			}
		}
		setUser(tempUser);
	};

	const loadPregnancyData = async () => {
		const data = await getHealthPlanBabyPregnancy({
			patientId: userId,
		}).unwrap();

		if (data?.healthPlanUser?.healthPlanUserBaby) {
			setHealthPlanBabyPregnancy(data.healthPlanUser.healthPlanUserBaby);
		}
	};

	useEffectAsync(async () => {
		await loadUser();
		await loadPregnancyData();
	}, [userUpdated]);

	if (!user) {
		return (
			<div className={'py-2'}>
				<Loading />
			</div>
		);
	}

	const metaItems = {};
	if (user && user.meta) {
		for (const metaItem of user.meta) {
			metaItems[metaItem.key] = metaItem.value;
		}
	}

	let insuranceCompany = null;
	if (user.profile?.insuranceCompanyTermItem) {
		insuranceCompany =
			user.profile.insuranceCompanyTermItem.value2 +
			' - ' +
			user.profile.insuranceCompanyTermItem.value;
	}
	if (
		!insuranceCompany &&
		metaItems &&
		metaItems['insurance'] &&
		metaItems['insurance']['companyName']
	) {
		insuranceCompany = metaItems['insurance']['companyName'];
	}

	const handleOpenProfile = () => {
		if (!allowOpenProfile) {
			return;
		}

		setProfileOpen(true);
	};

	if (metaIsLoading) {
		return <Loading />;
	}

	return (
		<div className={styles.wrapper}>
			{profileOpen && (
				<TreatmentModal
					opened
					onClose={() => setProfileOpen(false)}
					patientId={userId}
					workplaceId={workplaceId}
					loggedUserId={loggedUserId}
				/>
			)}
			<div>
				<UserAvatarImage
					userId={userId}
					customClass={styles.patientPhoto}
					profileImageUrl={user?.profileImageUrl}
				/>
			</div>
			<div className='ml-3'>
				<div
					className={styles.patientName}
					onClick={handleOpenProfile}
					style={allowOpenProfile ? { cursor: 'pointer' } : {}}>
					{user?.profile?.fullName}
				</div>
				{showAge && user?.profile?.age && (
					<div className={styles.age}>
						<FirstUpper>{translation['base.age']}</FirstUpper>:{' '}
						{user?.profile?.age} {t('base.years.many')}
					</div>
				)}
				{showBirthDate && identification.value && (
					<div className={styles.birthNumber}>
						{t(identification.title)}: {identification.value}
					</div>
				)}
			</div>
			<div className='ml-4'>
				{showInsuranceCompany && (
					<div className={styles.item}>
						<div className={styles.insuranceTitle}>
							{translation['base.insuranceShort']}:
						</div>
						<div className={styles.insuranceValue}>
							{insuranceCompany ? (
								insuranceCompany
							) : (
								<span className={styles.notIdentified}>&nbsp;</span>
							)}
						</div>
					</div>
				)}
				{showAddress && (
					<div className={styles.item}>
						<div className={styles.addressTitle}>
							{translation['base.shared.address']}:
						</div>
						{showVerifyInformation ? (
							<div className={styles.addressValue}>
								{user?.profile?.address?.fullAddress ? (
									user.profile.address.fullAddress
								) : (
									<span className={styles.notIdentified}>
										{translation['base.notIdentified']}
									</span>
								)}
							</div>
						) : (
							<div className={styles.addressValue}>
								{user?.profile?.address?.fullAddress ? (
									user.profile.address.fullAddress
								) : (
									<span className={styles.notIdentified}>&nbsp;</span>
								)}
							</div>
						)}
					</div>
				)}
				{user?.parents?.map(({ profile, id }) => (
					<React.Fragment key={id}>
						<div className={styles.item}>
							<Text
								className={styles.addressTitle}
								tx={'base.shared.legalRepresentative'}
							/>
							<div className={styles.emailValue}>{profile?.fullName}</div>
						</div>
					</React.Fragment>
				))}
				{user?.partner == 3 && hasPersonActingOnBehalfOfAPatient && (
					<div className={styles.item}>
						<div className={styles.addressTitle}>
							{t(
								'component.patientProfilePersonActingOnBehalfOfAPatient.title',
							)}
							:
						</div>
						<div className={styles.emailValue}>
							{getPersonActingOnBehalfOfAPatient()}
						</div>
					</div>
				)}
				{showPhone && user?.formattedPhoneNumber && (
					<div className={styles.item}>
						<div className={styles.addressTitle}>
							{translation['base.phoneShort']}:
						</div>
						<div className={styles.phoneValue}>
							{user?.formattedPhoneNumber}
						</div>
					</div>
				)}
				{showEmail && user?.email && (
					<div className={styles.item}>
						<div className={styles.addressTitle}>
							{translation['base.shared.email']}:
						</div>
						<div className={styles.emailValue}>{user?.email}</div>
					</div>
				)}
				{user?.partner == 3 && (
					<PatientPsychoQuestionnaireWarning patientId={userId} />
				)}
				{!metaIsLoading && patientDiagnosis?.mainDiagnosis && (
					<div className={styles.item}>
						<div className={styles.addressTitle}>
							<FirstUpper>{t('base.mainDiagnosisShort')}</FirstUpper>:
						</div>
						<div className={styles.emailValue}>
							{patientDiagnosis?.mainDiagnosis?.label}
						</div>
					</div>
				)}
			</div>
			{showGender && user?.profile?.formattedGender && (
				<div className='ml-5'>
					<div className={styles.genderTitle}>
						{translation['base.shared.gender']}:
					</div>
				</div>
			)}
			{showGender && user?.profile?.formattedGender && (
				<div className='ml-5'>
					<div className={styles.email}>{user?.profile?.formattedGender}</div>
				</div>
			)}
			<div className='ml-5'>
				{showVerifyInformation && (user.vzpResult || user.csszResult) && (
					<>
						{user.vzpResult ? (
							<div className={styles.lastVerification}>
								<img src='/dist/icons/v2/badge-check.svg' alt='green check' />
								{translation['base.verified']}:{' '}
								<FormattedDate date={user.vzpResult.checkedAt} />
							</div>
						) : (
							<div>&nbsp;</div>
						)}
						{user.csszResult ? (
							<div className={styles.lastVerification}>
								{user.csszResult.result ? (
									<img src='/dist/icons/v2/badge-check.svg' alt='green check' />
								) : null}
								{user.csszResult.result
									? t('base.verified')
									: t('component.csszResult.addressNotVerified')}
								: <FormattedDate date={user.csszResult.checkedAt} />
							</div>
						) : (
							<div>&nbsp;</div>
						)}
						{showPhone && user?.formattedPhoneNumber && <div>&nbsp;</div>}
						{showEmail && user?.email && <div>&nbsp;</div>}
					</>
				)}
				{showVerifyInformation && (
					<>
						<div>
							{links && (links['verifyInsurance'] || false) && (
								<a
									href={links['verifyInsurance']}
									className={styles.verifyButton}>
									{user.vzpResult
										? translation['base.verifyInsuranceAgain']
										: translation['base.verifyInsurance']}
								</a>
							)}
						</div>
						<div>
							{' '}
							{links && (links['verifyAddress'] || false) && (
								<a
									href={links['verifyAddress']}
									className={styles.verifyButton}>
									{user.csszResult
										? translation['base.verifyAddressAgain']
										: translation['base.verifyAddress']}
								</a>
							)}
						</div>
						{showPhone && user?.formattedPhoneNumber && <div>&nbsp;</div>}
						{showEmail && user?.email && <div>&nbsp;</div>}
					</>
				)}
			</div>
			{healthPlanBabyPregnancy && (
				<div className={styles.pregnancyInfo}>
					{healthPlanBabyPregnancy?.gestionalAge && (
						<div className={styles.item}>
							<div className={styles.addressTitle}>
								{isLatam
									? t('component.patientMiniProfile.gestationalWeek')
									: t('component.patientMiniProfile.babyCzech.gestationalWeek')}
								:
							</div>
							<div className={styles.emailValue}>
								{healthPlanBabyPregnancy.gestionalAge}
							</div>
						</div>
					)}
					{healthPlanBabyPregnancy?.dateOfBirth && (
						<div className={styles.item}>
							<div className={styles.addressTitle}>
								{isLatam
									? t('component.patientMiniProfile.plannedDateOfBirth')
									: t(
											'component.patientMiniProfile.babyCzech.plannedDateOfBirth',
									  )}
								:
							</div>
							<div className={styles.emailValue}>
								<FormattedDate date={healthPlanBabyPregnancy.dateOfBirth} />
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
}
